import { Check, ChevronDown, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ModelOption } from '../types/chat';
import { ModelIcon } from './ModelIcon';

type ModelSelectorProps = {
  models: ModelOption[];
  selected: ModelOption | null;
  selectedAlias: string;
  loading: boolean;
  error: string;
  onSelect: (alias: string) => void;
};

export function ModelSelector({ models, selected, selectedAlias, loading, error, onSelect }: ModelSelectorProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="interactive flex h-10 max-w-[168px] items-center gap-2 rounded-xl border border-border bg-surface-raised px-3 text-sm text-primary hover:bg-surface-hover sm:max-w-[220px]"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        disabled={loading || models.length === 0}
      >
        {error ? <X className="size-4 text-danger" /> : <ModelIcon alias={selectedAlias} className="size-4 text-accent-hover" />}
        <span className="truncate">{loading ? 'Memuat…' : selected?.name || 'Model'}</span>
        <ChevronDown className={`ml-auto size-3.5 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40 hidden bg-black/65 max-sm:block" aria-hidden="true" onClick={() => setOpen(false)} />
          <div
          role="listbox"
          aria-label="Pilih model AI"
          className="model-popover absolute right-0 top-12 z-50 w-[min(320px,calc(100vw-24px))] overflow-hidden rounded-xl border border-border bg-surface-raised p-2 shadow-popover max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:w-full max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:px-4 max-sm:pb-[max(16px,env(safe-area-inset-bottom))] max-sm:pt-3"
        >
          <div className="mb-2 hidden h-1 w-10 rounded-full bg-border max-sm:mx-auto max-sm:block" />
          <p className="px-2 py-2 text-sm font-semibold text-primary">Pilih Model</p>
          <div className="max-h-[420px] overflow-y-auto">
            {models.map((model) => {
              const active = model.alias === selectedAlias;
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  key={model.alias}
                  className={`interactive flex min-h-14 w-full items-center gap-3 rounded-lg px-3 py-2 text-left ${
                    active ? 'border-l-2 border-accent bg-accent-soft' : 'hover:bg-surface-hover'
                  }`}
                  onClick={() => {
                    onSelect(model.alias);
                    setOpen(false);
                  }}
                >
                  <span className={`grid size-8 shrink-0 place-items-center rounded-lg ${active ? 'bg-accent/20 text-accent-hover' : 'bg-surface-active text-muted'}`}>
                    <ModelIcon alias={model.alias} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-primary">{model.name}</span>
                    <span className="block truncate text-xs text-muted">{model.description}</span>
                  </span>
                  {active && <Check className="size-4 text-accent-hover" />}
                </button>
              );
            })}
          </div>
          </div>
        </>
      )}
    </div>
  );
}
